import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Section, Heading, FaqList, TrialCTA } from "./Home";
import PricingCalculator from "../../components/site/PricingCalculator";
import { PILLARS, INTEGRATED, BUSINESS } from "../../components/site/content";
import { api, errMsg, useSEO, PLANS, money } from "../../lib/api";
import { LEGAL } from "./legalContent";

const PageHero = ({ eyebrow, title, text }) => (
  <section className="bg-[#0A192F] text-white relative overflow-hidden">
    <div className="absolute inset-0 grid-bg opacity-30" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <p className="eyebrow !text-sky-400">{eyebrow}</p>
      <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">{title}</h1>
      <p className="mt-5 text-base md:text-lg text-slate-300 max-w-2xl">{text}</p>
    </div>
  </section>
);

const Grid = ({ items }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
    {items.map((b) => (
      <div key={b.title} className="lift border border-slate-200 rounded-xl p-6 bg-white">
        <b.icon className="w-6 h-6 text-sky-600" />
        <h3 className="mt-4 font-semibold text-slate-900">{b.title}</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.text}</p>
      </div>
    ))}
  </div>
);

const Pillars = () => (
  <div className="grid md:grid-cols-3 gap-6">
    {PILLARS.map((p) => (
      <div key={p.title} className="border border-slate-200 rounded-xl p-7 bg-white">
        <div className="flex items-center justify-between"><p.icon className={`w-7 h-7 ${p.color}`} /><span className="font-mono text-xs text-slate-500">{p.std}</span></div>
        <h3 className="mt-5 text-xl font-semibold">{p.title}</h3>
        <ul className="mt-4 space-y-2">{p.items.map((i) => <li key={i} className="flex gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-emerald-600 mt-0.5" />{i}</li>)}</ul>
      </div>
    ))}
  </div>
);

export function Features() {
  useSEO("Features", "Explore QORDEL 360 features: NCR management, corrective actions, audit management, risk assessment, incident reporting, document control and business tools.");
  return (
    <>
      <PageHero eyebrow="Features" title="Everything your management system needs, in one place." text="QORDEL 360 brings quality, safety, environmental and business records together with consistent, easy-to-learn registers." />
      <Section><Heading eyebrow="Core IMS" title="Built around ISO 9001, ISO 45001 and ISO 14001 processes" /><Pillars /></Section>
      <Section className="bg-slate-50 border-y border-slate-200"><Heading eyebrow="Integrated tools" title="Shared tools across your whole system" /><Grid items={INTEGRATED} /></Section>
      <Section><Heading eyebrow="Business tools" title="More Than Compliance — Business Management Tools Included." /><Grid items={BUSINESS} /></Section>
      <TrialCTA />
    </>
  );
}

export function Ims() {
  useSEO("IMS & QMS Software", "Integrated management system software for Australia. ISO 9001 quality management, ISO 45001 safety and ISO 14001 environmental management software with audits, NCRs and risk registers.");
  return (
    <>
      <PageHero eyebrow="IMS / QMS" title="Integrated management system software for Australian businesses." text="Manage quality, occupational health and safety, and environmental processes with one set of registers, one action list and one dashboard." />
      <Section><Pillars /></Section>
      <Section className="bg-slate-50 border-y border-slate-200"><Heading eyebrow="Integrated management" title="Registers, documents, audits and reviews" text="Legal registers, document control with revision history, audit schedules, management reviews, training and risk registers are shared across all three systems." /><Grid items={INTEGRATED} /></Section>
      <Section>
        <div className="border-l-4 border-sky-600 bg-sky-50 p-6 rounded-r-lg max-w-3xl">
          <h3 className="font-semibold text-slate-900">A note on certification</h3>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">QORDEL 360 helps you organise and maintain management system records. Using the software does not by itself guarantee ISO certification or legal compliance — certification is awarded by an accredited certification body.</p>
        </div>
      </Section>
      <TrialCTA />
    </>
  );
}

export function BusinessTools() {
  useSEO("Business Tools", "Quotations, purchase orders, customer and supplier management, job management, production and scheduling included with QORDEL 360.");
  return (
    <>
      <PageHero eyebrow="Business tools" title="More Than Compliance — Business Management Tools Included." text="The same subscription includes practical tools to run day-to-day operations, connected to your quality and safety records." />
      <Section><Grid items={BUSINESS} /></Section>
      <Section className="bg-slate-50 border-y border-slate-200">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <img src="/screens/quotes.png" alt="QORDEL 360 quoting register" className="rounded-xl border border-slate-200 w-full" />
          <Heading eyebrow="Connected" title="Quotes, jobs and quality in one flow" text="Link complaints to customers, evaluations to suppliers and production to jobs, so business and compliance information stay connected." />
        </div>
      </Section>
      <TrialCTA />
    </>
  );
}

export function Pricing() {
  useSEO("Pricing", "QORDEL 360 pricing in AUD: $100 per month, $540 every six months or $1,000 per year, including one user. Additional users from $15 per month. 5-day free trial.");
  return (
    <>
      <PageHero eyebrow="Pricing" title="Simple pricing in Australian dollars." text="Every plan includes every module and one user. Add users as your team grows." />
      <Section>
        <div className="grid md:grid-cols-4 gap-6 mb-14">
          <div className="border border-slate-200 rounded-xl p-6" data-testid="plan-card-trial">
            <p className="font-semibold text-slate-900">5-Day Free Trial</p>
            <p className="mt-3 text-3xl font-bold font-heading">$0</p>
            <ul className="mt-4 space-y-1.5 text-sm text-slate-600"><li>Full access for 5 days</li><li>One organisation, one user</li><li>No credit card required</li><li>No automatic charge</li></ul>
          </div>
          {Object.entries(PLANS).map(([k, p]) => (
            <div key={k} className={`border rounded-xl p-6 ${k === "annual" ? "border-sky-600 ring-1 ring-sky-600" : "border-slate-200"}`} data-testid={`plan-card-${k}`}>
              <p className="font-semibold text-slate-900">{p.label} Plan {k === "annual" && <span className="ml-1 text-xs text-sky-700">Best value</span>}</p>
              <p className="mt-3 text-3xl font-bold font-heading">{money(p.base)}</p>
              <p className="text-sm text-slate-500">{p.period} · {money(p.base / p.months)}/mo equivalent</p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-600"><li>Includes one user</li><li>{p.billed}</li><li>Extra users {money(p.seat)} {p.period}</li></ul>
            </div>
          ))}
        </div>
        <PricingCalculator />
        <div className="mt-10 grid md:grid-cols-2 gap-6 text-sm text-slate-600">
          <div><h3 className="font-semibold text-slate-900 mb-2">Billing policy</h3><p>Additional users purchased during a subscription term are prorated for the remaining term and charged immediately. Licence reductions take effect immediately and are not refunded for the remaining term. Cancellations stop the next renewal; access continues until the end of the paid period.</p></div>
          <div><h3 className="font-semibold text-slate-900 mb-2">GST</h3><p>All prices are in Australian dollars. GST treatment will be confirmed before live payments are activated.</p></div>
        </div>
      </Section>
      <Section className="bg-slate-50 border-t border-slate-200"><div className="grid lg:grid-cols-3 gap-12"><Heading eyebrow="FAQ" title="Pricing questions" /><FaqList className="lg:col-span-2" /></div></Section>
    </>
  );
}

export function About() {
  useSEO("About", "About QORDEL 360, integrated quality, safety and compliance management software for Australian businesses.");
  return (
    <>
      <PageHero eyebrow="About" title="Making management systems simpler." text="QORDEL 360 grew out of a working ERP and management system used every day in an Australian business." />
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-5 text-slate-600 leading-relaxed">
            <p>We built QORDEL 360 for small and medium businesses that need to manage quality, safety and environmental responsibilities without a dedicated compliance department.</p>
            <p>Our focus is simple: fewer spreadsheets, fewer mandatory fields, and records that are easy to find when an auditor, customer or regulator asks.</p>
            <p>We are a software provider. We do not certify businesses, and we do not claim that using our software guarantees certification or compliance.</p>
          </div>
          <img src="/img/team.jpg" alt="Team reviewing work together" className="rounded-xl w-full h-80 object-cover" />
        </div>
      </Section>
      <TrialCTA />
    </>
  );
}

export function Contact() {
  useSEO("Contact", "Contact the QORDEL 360 team for sales and support enquiries.");
  const [f, setF] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", f);
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  const inp = (k, label, type = "text") => (
    <label className="block"><span className="text-sm font-medium text-slate-700">{label}</span>
      <input type={type} required={k !== "company"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="field mt-1.5" data-testid={`contact-${k}-input`} /></label>
  );
  return (
    <>
      <PageHero eyebrow="Contact" title="Talk to us." text="Questions about QORDEL 360, pricing or your trial? Send us a message and we'll get back to you." />
      <Section>
        <div className="grid lg:grid-cols-2 gap-12">
          {sent ? (
            <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-8" data-testid="contact-success"><h3 className="text-xl font-semibold">Thanks — message received.</h3><p className="mt-2 text-slate-600">We'll reply to {f.email} as soon as possible.</p></div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              {inp("name", "Name")}{inp("email", "Email", "email")}{inp("company", "Company (optional)")}
              <label className="block"><span className="text-sm font-medium text-slate-700">Message</span>
                <textarea required rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="field mt-1.5" data-testid="contact-message-input" /></label>
              <button disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white font-semibold px-6 py-3 rounded-md" data-testid="contact-submit-btn">{busy ? "Sending…" : "Send message"}</button>
            </form>
          )}
          <div className="space-y-4 text-slate-600">
            <h3 className="text-lg font-semibold text-slate-900">Existing customers</h3>
            <p>Log in and open <strong>Support</strong> from the app menu to raise a support, billing or data request linked to your organisation.</p>
            <Link to="/login" className="inline-block font-semibold text-sky-700">Customer login →</Link>
          </div>
        </div>
      </Section>
    </>
  );
}

export function Legal() {
  const { doc } = useParams();
  const d = LEGAL[doc] || LEGAL.privacy;
  useSEO(d.title, d.title + " for QORDEL 360.");
  return (
    <Section>
      <div className="max-w-3xl" data-testid="legal-doc">
        <div className="mb-8 border border-amber-300 bg-amber-50 text-amber-900 text-sm rounded-lg p-4">DRAFT — this document is a draft prepared for review and must be reviewed by a qualified legal professional before commercial launch.</div>
        <h1 className="text-4xl font-bold tracking-tight">{d.title}</h1>
        <div className="mt-8 space-y-6">
          {d.sections.map(([h, t]) => (<div key={h}><h2 className="text-lg font-semibold text-slate-900">{h}</h2><p className="mt-2 text-slate-600 leading-relaxed">{t}</p></div>))}
        </div>
      </div>
    </Section>
  );
}
